import { useQuery } from '@tanstack/react-query';
import { ClipboardList, HeartHandshake, UserCheck, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { SimpleDonutChart } from '@/components/shared/simple-donut-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { formatNumber } from '@/utils/format';

interface SoulWonRow {
  id: string;
  firstName: string;
  lastName: string;
  personName: string;
  wonOn: string;
  decisionType: 'salvation' | 'rededication' | 'baptism' | 'other';
  followUpId: string | null;
  convertedMemberId: string | null;
}

interface EvangelismSettings {
  showLeaderboard: boolean;
}

interface EvangelismTotals {
  periodTotal: number;
  openCount: number;
  followUpCount: number;
  convertedCount: number;
  byDecision: Array<{
    decisionType: SoulWonRow['decisionType'];
    count: number;
  }>;
}

export const SoulsWonPage = () => {
  const { t } = useTranslation('evangelism');
  const navigate = useNavigate();

  const list = useQuery({
    queryKey: ['evangelism', 'souls-won'],
    queryFn: () => api.get<{ data: SoulWonRow[] }>('/evangelism/souls-won'),
  });

  const settings = useQuery({
    queryKey: ['evangelism', 'settings'],
    queryFn: () => api.get<EvangelismSettings>('/evangelism/settings'),
  });

  const totals = useQuery({
    queryKey: ['evangelism', 'totals'],
    queryFn: () => api.get<EvangelismTotals>('/evangelism/totals'),
  });

  const rows = list.data?.data ?? [];

  const toggleLeaderboard = async (showLeaderboard: boolean) => {
    await api.patch('/evangelism/settings', { showLeaderboard });
    await settings.refetch();
  };

  const columns: DataTableColumn<SoulWonRow>[] = [
    {
      id: 'date',
      header: t('columns.date'),
      cell: (row) => row.wonOn,
    },
    {
      id: 'person',
      header: t('columns.person'),
      cell: (row) => (
        <MemberNameLink memberId={row.convertedMemberId} name={row.personName} />
      ),
    },
    {
      id: 'decision',
      header: t('columns.decision'),
      cell: (row) => t(`decisionType.${row.decisionType}`),
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => (
        <div className="flex flex-wrap gap-2">
          {row.followUpId ? <Badge variant="neutral">{t('status.followUp')}</Badge> : null}
          {row.convertedMemberId ? (
            <Badge variant="success">{t('status.converted')}</Badge>
          ) : null}
          {!row.followUpId && !row.convertedMemberId ? (
            <Badge variant="neutral">{t('status.open')}</Badge>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="evangelism.purpose"
        headerActions={
          <Button asChild>
            <Link to="/evangelism/new">{t('record')}</Link>
          </Button>
        }
      />

      <CollapsibleInsights
        storageKey="trinity.insights.evangelism.open"
        summary={[
          {
            label: t('metrics.total'),
            value: totals.data ? formatNumber(totals.data.periodTotal) : '…',
          },
          {
            label: t('metrics.open'),
            value: totals.data ? formatNumber(totals.data.openCount) : '…',
          },
          {
            label: t('metrics.followUp'),
            value: totals.data ? formatNumber(totals.data.followUpCount) : '…',
          },
          {
            label: t('metrics.converted'),
            value: totals.data ? formatNumber(totals.data.convertedCount) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={t('metrics.total')}
            value={totals.data ? formatNumber(totals.data.periodTotal) : undefined}
            icon={<HeartHandshake className="size-4" aria-hidden />}
            loading={totals.isLoading}
          />
          <StatCard
            label={t('metrics.open')}
            value={totals.data ? formatNumber(totals.data.openCount) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={totals.isLoading}
          />
          <StatCard
            label={t('metrics.followUp')}
            value={totals.data ? formatNumber(totals.data.followUpCount) : undefined}
            icon={<ClipboardList className="size-4" aria-hidden />}
            loading={totals.isLoading}
          />
          <StatCard
            label={t('metrics.converted')}
            value={totals.data ? formatNumber(totals.data.convertedCount) : undefined}
            icon={<UserCheck className="size-4" aria-hidden />}
            loading={totals.isLoading}
          />
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <ChartCard
            title={t('metrics.byDecision')}
            loading={totals.isLoading}
            hasData={(totals.data?.byDecision.length ?? 0) > 0}
            empty={{ title: t('metrics.byDecisionEmpty') }}
            height={180}
          >
            <SimpleDonutChart
              data={(totals.data?.byDecision ?? []).map((row) => ({
                label: t(`decisionType.${row.decisionType}`),
                value: row.count,
              }))}
              formatValue={formatNumber}
              size={140}
            />
          </ChartCard>
        </div>
      </CollapsibleInsights>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-surface p-4">
        <div>
          <p className="text-body font-medium">{t('leaderboard.title')}</p>
          <p className="text-caption text-fg-secondary">{t('leaderboard.description')}</p>
        </div>
        <label className="flex items-center gap-2 text-caption">
          <Switch
            checked={settings.data?.showLeaderboard ?? false}
            disabled={settings.isLoading}
            onCheckedChange={(checked) => void toggleLeaderboard(checked)}
          />
          {settings.data?.showLeaderboard ? t('leaderboard.hide') : t('leaderboard.show')}
        </label>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <p className="text-body font-medium">{t('empty.title')}</p>
            <p className="mt-1 text-caption text-fg-secondary">{t('empty.description')}</p>
            <div className="mt-4 flex justify-end sm:justify-center">
              <Button asChild>
                <Link to="/evangelism/new">{t('empty.action')}</Link>
              </Button>
            </div>
          </div>
        }
        onRowClick={(row) => void navigate(`/evangelism/${row.id}`)}
      />
    </div>
  );
};
